import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Sparkles,
  Lock,
  Check,
  Loader2,
  Clock,
  ArrowRight,
  Copy,
  Download,
  Printer,
  FileDown,
  Info,
  Trash2,
  ShieldCheck,
  AlertTriangle,
} from "lucide-react";
import SEO, { orgSchema, breadcrumbSchema } from "@/components/SEO";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Generator() {
  const [stage, setStage] = useState("select"); // select | interview | generating | preview
  const [types, setTypes] = useState([]);
  const [docId, setDocId] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [preview, setPreview] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    axios
      .get(`${API}/documents/types`)
      .then((r) => setTypes(r.data))
      .catch(() => toast.error("No pudimos cargar los documentos"));
  }, []);

  // Abrir directamente un documento ya generado al recibir ?ver=DOC_ID desde /cuenta
  useEffect(() => {
    const verId = searchParams.get("ver");
    const tipoQuery = searchParams.get("tipo");
    const retomarId = searchParams.get("retomar");

    if (verId) {
      (async () => {
        try {
          const { data } = await axios.get(`${API}/documents/${verId}/preview`);
          setDocId(verId);
          setPreview(data);
          setStage("preview");
          window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
          toast.error(e?.response?.data?.detail || "No pudimos abrir ese documento");
        }
        searchParams.delete("ver");
        setSearchParams(searchParams, { replace: true });
      })();
      return;
    }

    if (tipoQuery && types.length > 0) {
      // Si el tipo viene del query, lanzar el wizard directamente
      const t = types.find((x) => x.id === tipoQuery);
      if (t && t.disponible) {
        startDoc(tipoQuery);
        searchParams.delete("tipo");
        setSearchParams(searchParams, { replace: true });
      }
      return;
    }

    if (retomarId) {
      (async () => {
        try {
          // Primero ver si está generado → ir directo a preview
          const { data: status } = await axios.get(`${API}/documents/${retomarId}/status`);
          if (status.estado === "generado") {
            const { data: prev } = await axios.get(`${API}/documents/${retomarId}/preview`);
            setDocId(retomarId);
            setPreview(prev);
            setStage("preview");
            window.scrollTo({ top: 0, behavior: "smooth" });
          } else {
            // Borrador o error → cargar respuestas y saltar al step correcto
            const { data: resume } = await axios.get(`${API}/documents/${retomarId}/resume`);
            setDocId(resume.id);
            setSelectedType({ id: resume.tipo, nombre: resume.nombre });
            setQuestions(resume.preguntas);
            setAnswers(resume.respuestas || {});
            setStepIdx(resume.step_idx || 0);
            setStage("interview");
            window.scrollTo({ top: 0, behavior: "smooth" });
            toast.info(`Retomando "${resume.nombre}" donde lo dejaste`);
          }
        } catch (e) {
          toast.error(e?.response?.data?.detail || "No pudimos abrir ese documento");
        }
        searchParams.delete("retomar");
        setSearchParams(searchParams, { replace: true });
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, types]);

  const startDoc = async (tipo) => {
    try {
      const { data } = await axios.post(`${API}/documents/start`, { tipo });
      setDocId(data.id);
      setSelectedType(data);
      setQuestions(data.preguntas);
      setStepIdx(0);
      setAnswers({});
      setStage("interview");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al iniciar el documento");
    }
  };

  const saveAndNext = async () => {
    const q = questions[stepIdx];
    if (q.required) {
      const v = answers[q.id];
      const empty =
        v === undefined ||
        v === "" ||
        (Array.isArray(v) && v.length === 0);
      if (empty) {
        toast.error("Esta pregunta es obligatoria");
        return;
      }
    }
    try {
      await axios.put(`${API}/documents/${docId}/answers`, {
        respuestas: { [q.id]: answers[q.id] ?? "" },
      });
    } catch (e) {
      toast.error("No pudimos guardar tu respuesta");
      return;
    }

    if (stepIdx < questions.length - 1) {
      setStepIdx(stepIdx + 1);
    } else {
      // Generate (background) + poll status
      setStage("generating");
      try {
        await axios.post(`${API}/documents/${docId}/generate`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error al iniciar la generación");
        setStage("interview");
        return;
      }

      // Polling
      const startedAt = Date.now();
      const maxWaitMs = 4 * 60 * 1000; // 4 min
      while (Date.now() - startedAt < maxWaitMs) {
        await new Promise((r) => setTimeout(r, 3500));
        try {
          const { data: status } = await axios.get(`${API}/documents/${docId}/status`);
          if (status.estado === "generado") {
            const { data } = await axios.get(`${API}/documents/${docId}/preview`);
            setPreview(data);
            setStage("preview");
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
          }
          if (status.estado === "error") {
            toast.error(status.error_message || "No pudimos generar el documento. Intenta de nuevo.");
            setStage("interview");
            return;
          }
        } catch (e) {
          // ignore transient errors and keep polling
        }
      }
      toast.error("La generación tardó demasiado. Por favor reintenta.");
      setStage("interview");
    }
  };

  const back = () => {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  };

  return (
    <>
      <SEO
        title="Generador de Política de Privacidad — Conforme a la Ley 21.719"
        description="Genera tu Política de Privacidad personalizada para Chile en 7 minutos. Conforme a la Ley N° 21.719 de Protección de Datos Personales. Vista previa gratis."
        path="/cuenta/generar"
        keywords="política de privacidad Chile, Ley 21.719, generador política privacidad, PYME, plantilla privacidad"
        schemas={[
          orgSchema,
          breadcrumbSchema([
            { name: "Inicio", path: "/" },
            { name: "Generar documentos", path: "/cuenta/generar" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Ley de Datos Chile Pro — Ley 21.719",
            description:
              "Acceso al Módulo Pro: todos los documentos legales de la Ley 21.719 (Política de Privacidad, Cláusula de Consentimiento, DPA) + guías de implementación por plataforma. Pago único $14.990 · acceso permanente · alertas de la ley gratis de por vida.",
            offers: {
              "@type": "Offer",
              priceCurrency: "CLP",
              price: "14990",
              availability: "https://schema.org/InStock",
            },
          },
        ]}
      />

      <section className="px-6 py-12 sm:py-16 min-h-[70vh]">
        <div className="max-w-4xl mx-auto">
          {stage === "select" && (
            <SelectStage types={types} onPick={startDoc} />
          )}
          {stage === "interview" && questions.length > 0 && (
            <InterviewStage
              q={questions[stepIdx]}
              idx={stepIdx}
              total={questions.length}
              docName={selectedType?.nombre}
              value={answers[questions[stepIdx].id]}
              onChange={(v) =>
                setAnswers({ ...answers, [questions[stepIdx].id]: v })
              }
              onNext={saveAndNext}
              onBack={back}
            />
          )}
          {stage === "generating" && <GeneratingStage />}
          {stage === "preview" && preview && (
            <PreviewStage
              preview={preview}
              onRestart={() => {
                setStage("select");
                setDocId(null);
                setPreview(null);
                setAnswers({});
              }}
            />
          )}
        </div>
      </section>
    </>
  );
}

function SelectStage({ types, onPick }) {
  return (
    <div>
      <p className="text-xs font-semibold tracking-widest uppercase text-[#1E3A8A]">
        Generador de documentos
      </p>
      <h1
        data-testid="generator-title"
        className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900"
      >
        Genera tus documentos de la Ley 21.719
      </h1>
      <p className="mt-4 text-lg text-slate-600 max-w-2xl leading-relaxed">
        Respondes una entrevista guiada simple y nuestra IA redacta tu
        documento personalizado conforme a la Ley 21.719. Vista previa gratis.
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
        {types.map((t) => (
          <button
            key={t.id}
            data-testid={`doc-type-${t.id}`}
            onClick={() => t.disponible && onPick(t.id)}
            disabled={!t.disponible}
            className={`text-left p-7 rounded-2xl border-2 transition-all ${
              t.disponible
                ? "bg-white border-slate-200 hover:border-[#1E3A8A] hover:shadow-md cursor-pointer"
                : "bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#1E3A8A]/10 grid place-items-center">
                <FileText className="w-5 h-5 text-[#1E3A8A]" />
              </div>
              {!t.disponible && (
                <span className="text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-slate-200 text-slate-600">
                  Próximamente
                </span>
              )}
            </div>
            <h3 className="mt-5 font-display text-xl font-semibold text-slate-900">
              {t.nombre}
            </h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              {t.descripcion}
            </p>
            <div className="mt-5 flex items-center gap-4 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {t.duracion_estimada_min} min
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-[#1E3A8A]" />
                Incluido en el Pro
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function InterviewStage({ q, idx, total, docName, value, onChange, onNext, onBack }) {
  const progress = ((idx + 1) / total) * 100;

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center justify-between text-xs font-semibold tracking-wider uppercase text-slate-500 mb-3">
          <span>{docName}</span>
          <span data-testid="interview-progress">
            Pregunta {idx + 1} de {total}
          </span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-[#1E3A8A]"
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={q.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-start gap-2">
            <h2
              data-testid="question-label"
              className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight flex-1"
            >
              {q.label}
              {q.required && <span className="text-rose-600 ml-1">*</span>}
            </h2>
            {q.tooltip && (
              <TooltipProvider delayDuration={120}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      data-testid={`question-tooltip-${q.id}`}
                      aria-label="Más información sobre esta pregunta"
                      className="mt-1 w-7 h-7 rounded-full grid place-items-center text-slate-400 hover:text-[#1E3A8A] hover:bg-[#1E3A8A]/5 transition-colors shrink-0"
                    >
                      <Info className="w-4.5 h-4.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    align="end"
                    className="max-w-sm bg-slate-900 text-white p-3 text-xs leading-relaxed shadow-xl border-0"
                  >
                    {q.tooltip}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {q.help && (
            <p className="mt-3 text-sm text-slate-500 leading-relaxed">{q.help}</p>
          )}

          <div className="mt-8">
            <QuestionInput q={q} value={value} onChange={onChange} />
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="mt-10 flex items-center justify-between gap-4">
        <button
          data-testid="interview-back"
          onClick={onBack}
          disabled={idx === 0}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Atrás
        </button>
        <button
          data-testid="interview-next"
          onClick={onNext}
          className="inline-flex items-center gap-2 h-12 px-7 rounded-lg bg-[#1E3A8A] hover:bg-[#1E40AF] active:scale-[0.98] text-white font-semibold transition-all"
        >
          {idx === total - 1 ? "Generar documento" : "Siguiente"}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function QuestionInput({ q, value, onChange }) {
  if (q.type === "text" || q.type === "email") {
    return (
      <input
        data-testid={`q-input-${q.id}`}
        type={q.type}
        placeholder={q.placeholder || ""}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-14 px-5 text-lg bg-white border-2 border-slate-200 rounded-xl focus:border-[#1E3A8A] focus:outline-none transition-colors"
        autoFocus
      />
    );
  }
  if (q.type === "textarea") {
    return (
      <textarea
        data-testid={`q-input-${q.id}`}
        placeholder={q.placeholder || ""}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="w-full p-5 text-base bg-white border-2 border-slate-200 rounded-xl focus:border-[#1E3A8A] focus:outline-none resize-none transition-colors"
        autoFocus
      />
    );
  }
  if (q.type === "radio") {
    return (
      <div className="space-y-3" role="radiogroup">
        {q.options.map((opt) => {
          const selected = value === opt;
          return (
            <button
              key={opt}
              data-testid={`q-opt-${q.id}-${opt}`}
              type="button"
              onClick={() => onChange(opt)}
              className={`w-full text-left p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                selected
                  ? "border-[#1E3A8A] bg-[#1E3A8A]/5"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <span
                className={`w-5 h-5 rounded-full border-2 grid place-items-center ${
                  selected ? "border-[#1E3A8A]" : "border-slate-300"
                }`}
              >
                {selected && (
                  <span className="w-2.5 h-2.5 rounded-full bg-[#1E3A8A]" />
                )}
              </span>
              <span className="text-base text-slate-900">{opt}</span>
            </button>
          );
        })}
      </div>
    );
  }
  if (q.type === "checkbox") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      const next = arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt];
      onChange(next);
    };
    return (
      <div className="space-y-3">
        {q.options.map((opt) => {
          const checked = arr.includes(opt);
          return (
            <button
              key={opt}
              data-testid={`q-opt-${q.id}-${opt}`}
              type="button"
              onClick={() => toggle(opt)}
              className={`w-full text-left p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                checked
                  ? "border-[#1E3A8A] bg-[#1E3A8A]/5"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <span
                className={`w-5 h-5 rounded-md border-2 grid place-items-center transition-colors ${
                  checked ? "border-[#1E3A8A] bg-[#1E3A8A]" : "border-slate-300"
                }`}
              >
                {checked && <Check className="w-3.5 h-3.5 text-white" />}
              </span>
              <span className="text-base text-slate-900">{opt}</span>
            </button>
          );
        })}
      </div>
    );
  }
  return null;
}

function GeneratingStage() {
  const messages = [
    "Analizando tus respuestas…",
    "Consultando la Ley 21.719…",
    "Redactando tu Política de Privacidad…",
    "Revisando estructura y secciones obligatorias…",
    "Casi listo…",
  ];
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % messages.length), 2400);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center">
      <div className="w-20 h-20 rounded-full bg-[#1E3A8A]/5 grid place-items-center mb-8">
        <Loader2 className="w-9 h-9 text-[#1E3A8A] animate-spin" />
      </div>
      <p className="text-xs font-semibold tracking-widest uppercase text-slate-500">
        Generando documento
      </p>
      <AnimatePresence mode="wait">
        <motion.h2
          key={i}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.4 }}
          className="mt-3 font-display text-2xl font-semibold text-slate-900"
        >
          {messages[i]}
        </motion.h2>
      </AnimatePresence>
      <p className="mt-8 text-sm text-slate-400">
        Esto puede tomar 60-120 segundos. Estamos redactando el documento completo.
      </p>
    </div>
  );
}

async function downloadDocument(docId, format, nombre) {
  try {
    const url = format === "pdf" ? `${API}/documents/${docId}/pdf` : `${API}/documents/${docId}/download?format=${format}`;
    const res = await axios.get(url, { responseType: "blob" });
    const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    const slug = (nombre || "documento").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    a.download = `${slug}-leydedatoschile.${format}`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(a.href);
      a.remove();
    }, 500);
    toast.success(`Descargando ${format.toUpperCase()}…`);
  } catch (e) {
    const msg = e?.response?.data?.detail || "No pudimos descargar el documento";
    toast.error(typeof msg === "string" ? msg : "Error al descargar");
  }
}

function PreviewStage({ preview, onRestart }) {
  const navigate = useNavigate();
  const [pdfLoading, setPdfLoading] = useState(false);
  const desbloqueado = !!preview.desbloqueado;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(preview.preview);
      toast.success("Texto copiado al portapapeles");
    } catch {
      toast.error("Tu navegador no permitió copiar. Usa la descarga.");
    }
  };

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      await downloadDocument(preview.id, "pdf", preview.nombre);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-emerald-700">
            ✓ Documento generado
          </p>
          <h1
            data-testid="preview-title"
            className="mt-2 font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight"
          >
            {preview.nombre}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {preview.palabras_aproximadas.toLocaleString("es-CL")} palabras
            {desbloqueado ? " · Documento completo" : " · Cuerpo bloqueado · descarga con Pro"}
          </p>
          <AuditBadge auditoria={preview.auditoria} />
          {desbloqueado && Array.isArray(preview.defaults_inferidos) && preview.defaults_inferidos.length > 0 && (
            <DefaultsInferidosCard fields={preview.defaults_inferidos} />
          )}
        </div>
        <button
          data-testid="preview-restart"
          onClick={onRestart}
          className="text-sm font-medium text-slate-600 hover:text-slate-900"
        >
          ← Generar otro
        </button>
      </div>

      {/* Barra de acciones — solo si está desbloqueado */}
      {desbloqueado && (
        <div
          data-testid="action-bar"
          className="sticky top-16 z-10 mb-5 bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-wrap items-center gap-2"
        >
          <span className="text-xs text-slate-500 mr-1 flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
            Acceso Pro activo · descarga ilimitada
          </span>
          <div className="ml-auto flex flex-wrap gap-2">
            <button
              data-testid="action-copy"
              onClick={copyToClipboard}
              className="h-10 px-4 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-1.5"
            >
              <Copy className="w-4 h-4" />
              Copiar texto
            </button>
            <button
              data-testid="action-download-md"
              onClick={() => downloadDocument(preview.id, "md", preview.nombre)}
              className="h-10 px-4 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-1.5"
            >
              <Download className="w-4 h-4" />
              .md
            </button>
            <button
              data-testid="action-download-txt"
              onClick={() => downloadDocument(preview.id, "txt", preview.nombre)}
              className="h-10 px-4 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-1.5"
            >
              <Download className="w-4 h-4" />
              .txt
            </button>
            <button
              data-testid="action-download-pdf"
              onClick={handlePdf}
              disabled={pdfLoading}
              className="h-10 px-4 rounded-lg bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              Descargar PDF
            </button>
            <button
              data-testid="action-print"
              onClick={() => window.print()}
              className="h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-1.5"
              title="Imprimir"
            >
              <Printer className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <article
        data-testid="preview-content"
        className="relative prose prose-slate max-w-none prose-headings:font-display prose-headings:tracking-tight prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3 prose-h3:text-lg prose-p:leading-relaxed prose-table:text-sm bg-white border border-slate-200 rounded-2xl p-8 sm:p-10"
      >
        {!desbloqueado && (
          <div className="not-prose mb-6 pb-6 border-b border-slate-200">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase">
                <Lock className="w-3 h-3" />
                Vista previa gated · cuerpo bloqueado
              </div>
              <div className="text-xs text-slate-500 font-mono">
                {preview.palabras_aproximadas.toLocaleString("es-CL")} palabras · {preview.secciones_count || 0} secciones
              </div>
            </div>
            {Array.isArray(preview.secciones) && preview.secciones.length > 0 && (
              <>
                <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-3">Tabla de contenidos</p>
                <ul className="space-y-1.5 mb-2">
                  {preview.secciones.map((s, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                      <span className="font-mono text-[10px] text-slate-400 tabular-nums w-6">{String(i + 1).padStart(2, "0")}</span>
                      <Lock className="w-3 h-3 text-slate-400 shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
        <div className={!desbloqueado ? "relative select-none" : ""}>
          <ReactMarkdown>{preview.preview}</ReactMarkdown>
          {!desbloqueado && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-white"
              style={{ filter: "blur(0.5px)" }}
            />
          )}
        </div>
        {!desbloqueado && (
          <p className="not-prose mt-4 text-[11px] font-mono text-slate-400 uppercase tracking-wider">
            ▾ {Math.max(0, 100 - (preview.porcentaje_visible || 0)).toFixed(0)}% del contenido restante disponible con Pro
          </p>
        )}
      </article>

      {/* Paywall — solo si NO está desbloqueado */}
      {!desbloqueado && (
        <div
          data-testid="paywall-section"
          className="mt-8 bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white rounded-2xl p-8 sm:p-12 shadow-xl relative overflow-hidden"
        >
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/5 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold uppercase tracking-wide">
              <Lock className="w-3.5 h-3.5" />
              Documento listo · descarga bloqueada
            </div>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Desbloquea el Módulo Pro
            </h2>
            <p className="mt-3 text-white/80 max-w-xl text-base leading-relaxed">
              Este documento ya está generado en tu cuenta. Con Pro accedes al cuerpo completo, lo descargas en PDF/Markdown/TXT y desbloqueas los otros 4 documentos de la Ley 21.719 más las guías por plataforma.
            </p>

            <div className="mt-8 max-w-md bg-white/10 backdrop-blur rounded-xl p-6 border border-white/10">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/60">
                Ley de Datos Chile Pro
              </p>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display text-4xl font-bold">$14.990</span>
                <span className="text-sm text-white/70">CLP · una vez</span>
              </div>

              <ul className="mt-5 space-y-2 text-sm text-white/90">
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-white/70" />Política de Privacidad</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-white/70" />Cláusula de Consentimiento</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-white/70" />Contrato con Encargado de Datos (DPA)</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-white/70" />Catálogo de Datos Personales (RAT)</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-white/70" />Descarga en PDF, Markdown y TXT</li>
              </ul>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="paywall-buy-btn"
                onClick={() => navigate("/cuenta")}
                className="h-12 px-7 rounded-lg bg-white text-[#1E3A8A] font-semibold hover:bg-slate-100 active:scale-[0.98] transition-all inline-flex items-center gap-2"
              >
                Desbloquear Pro — $14.990
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="paywall-back-home"
                onClick={() => navigate("/")}
                className="h-12 px-6 rounded-lg border border-white/30 text-white hover:bg-white/10 font-medium transition-colors"
              >
                Volver al inicio
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comparativa */}
      <div className="mt-12">
        <h3 className="font-display text-2xl font-bold text-slate-900 tracking-tight">
          Por qué Ley de Datos Chile
        </h3>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <ComparativeColumn
            title="Abogado tradicional"
            highlight={false}
            items={[
              ["Precio", "Alto ($300k+)"],
              ["Tiempo", "Días o semanas"],
              ["Adaptado a tu negocio", "Sí"],
              ["Conforme Ley 21.719", "Sí"],
            ]}
          />
          <ComparativeColumn
            title="Ley de Datos Chile Pro"
            highlight
            items={[
              ["Precio", "$14.990 — pago único · acceso permanente"],
              ["Tiempo", "Minutos"],
              ["Adaptado a tu negocio", "Sí, según tus respuestas"],
              ["Conforme Ley 21.719", "Sí"],
              ["Incluye", "Todos los documentos + guías"],
            ]}
          />
          <ComparativeColumn
            title="Plantilla de internet"
            highlight={false}
            items={[
              ["Precio", "Gratis"],
              ["Tiempo", "Inmediato"],
              ["Adaptado a tu negocio", "No"],
              ["Conforme Ley 21.719", "Casi nunca"],
            ]}
          />
        </div>
      </div>
    </div>
  );
}

function ComparativeColumn({ title, items, highlight }) {
  return (
    <div
      className={`rounded-2xl p-6 ${
        highlight
          ? "bg-[#1E3A8A] text-white border-2 border-[#1E3A8A] shadow-lg scale-[1.02]"
          : "bg-white border border-slate-200"
      }`}
    >
      {highlight && (
        <div className="inline-block px-2.5 py-0.5 rounded-full bg-white text-[#1E3A8A] text-[10px] font-bold uppercase tracking-wider mb-3">
          Recomendado
        </div>
      )}
      <h4
        className={`font-display text-lg font-semibold ${
          highlight ? "text-white" : "text-slate-900"
        }`}
      >
        {title}
      </h4>
      <dl className="mt-4 space-y-3">
        {items.map(([label, value]) => (
          <div key={label}>
            <dt
              className={`text-[11px] uppercase tracking-wider font-semibold ${
                highlight ? "text-white/60" : "text-slate-500"
              }`}
            >
              {label}
            </dt>
            <dd
              className={`text-sm mt-0.5 ${
                highlight ? "text-white" : "text-slate-700"
              }`}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}


function AuditBadge({ auditoria }) {
  if (!auditoria) {
    return (
      <div data-testid="audit-pending" className="mt-3 inline-flex items-center gap-2 text-xs text-slate-500">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Auditando cobertura legal con IA…
      </div>
    );
  }
  const cobertura = Number(auditoria.cobertura_pct || 0);
  const veredicto = auditoria.veredicto || "revisar";
  const palette =
    veredicto === "completo"
      ? { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", icon: ShieldCheck, iconColor: "text-emerald-600", label: "Auditoría: cobertura legal completa" }
      : veredicto === "incompleto"
      ? { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-900", icon: AlertTriangle, iconColor: "text-rose-600", label: "Auditoría: faltan secciones obligatorias" }
      : { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", icon: AlertTriangle, iconColor: "text-amber-600", label: "Auditoría: revisar antes de publicar" };
  const Icon = palette.icon;
  return (
    <div data-testid="audit-badge" className={`mt-3 rounded-xl border ${palette.border} ${palette.bg} p-3 max-w-2xl`}>
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${palette.iconColor}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <p className={`text-xs font-semibold uppercase tracking-wider ${palette.text}`}>{palette.label}</p>
            <span className={`text-xs font-bold ${palette.text}`}>{cobertura}%</span>
          </div>
          {auditoria.resumen && <p className={`mt-1 text-xs ${palette.text} opacity-90`}>{auditoria.resumen}</p>}
          {auditoria.secciones_faltantes && auditoria.secciones_faltantes.length > 0 && (
            <ul className={`mt-2 text-xs ${palette.text} list-disc pl-4 space-y-0.5`}>
              {auditoria.secciones_faltantes.slice(0, 4).map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// Mapa de labels legibles para los campos default inferidos por _essential_doc_defaults
const FIELD_LABELS = {
  tipo_formulario: "Tipo de formulario",
  datos_solicitados: "Datos solicitados",
  finalidad_especifica: "Finalidad específica",
  usa_para_marketing: "Uso para marketing",
  comparte_con_terceros: "Compartir con terceros",
  menores: "Datos de menores",
  tiene_dpo: "Tiene DPO",
  categorias_titulares: "Categorías de titulares",
  trata_datos_sensibles: "Trata datos sensibles",
  transferencias_internacionales: "Transferencias internacionales",
  medidas_tecnicas: "Medidas técnicas",
  medidas_organizativas: "Medidas organizativas",
  brechas_ultimos_12m: "Brechas últimos 12 meses",
  comentarios: "Comentarios",
};

function DefaultsInferidosCard({ fields }) {
  if (!fields || fields.length === 0) return null;
  return (
    <div
      data-testid="defaults-inferidos-card"
      className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/60 p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-700 grid place-items-center shrink-0">
          <AlertTriangle className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-amber-700 mb-1.5">
            Defaults inferidos · {fields.length} campo{fields.length === 1 ? "" : "s"}
          </p>
          <p className="text-sm text-slate-800 leading-relaxed">
            Estos campos no estaban en tu onboarding y los completamos con valores conservadores inferidos. <strong className="text-slate-900">Revísalos antes de publicar</strong>.
          </p>
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {fields.map((f) => (
              <li
                key={f}
                className="font-mono text-[10px] tracking-wider uppercase px-2 py-0.5 rounded bg-white border border-amber-200 text-amber-800"
              >
                {FIELD_LABELS[f] || f}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

